import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DataImportInfo } from "../../../api/types/dataImportTypes";

const discardDataImport = withErrorHandling(adminApi.discardDataImport);

interface Props {
  dataImport: DataImportInfo;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteDataImportDialog = ({ dataImport, onClose, onConfirm }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    const [result, error] = await discardDataImport(dataImport.id);
    setLoading(false);

    if (error || !result) {
      logError(error, "[DeleteDataImportDialog]");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Data import deleted successfully");
    onConfirm();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete Data Import</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete data import?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete <strong>{dataImport.description || dataImport.sourceDescription}</strong>.
              This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleSubmit}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteDataImportDialog;
